/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useContext, useMemo, createContext, FC, useEffect } from 'react';
import { PresentationElement, TrialRunnerPhase, ExperimentResults, TrialRunnerStage } from 'types';
import { useExperimentContext } from './ExperimentContext';

interface ModelState {
    weights: number[];
    t_mean: number;
    t_sd: number;
    s_mean: number;
    s_sd: number;
}

interface Props {}
let effectiveTrialIndex: number | undefined = 0;
let currentRow: any;

const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

const useValue = () => {
        const {
            srcToAudioMap,
            headerToIndexMap,
            stimulusData,
            currentStage: cs,
            experimentResults,
            setActiveTrialIdentifier
        } = useExperimentContext();

        // 1. ALL STATE HOOKS TOGETHER AT THE TOP (Satisfies React Hook Rules)
        const [modelState, setModelState] = useState<ModelState | null>(null);
        const [designCounts, setDesignCounts] = useState<Record<number, number>>({});
        const [currentPresentationIndex] = useState(0);
        const [currentTrialIndex, setCurrentTrialIndex] = useState(0);
        const [currentPhase, setCurrentPhase] = useState<TrialRunnerPhase>('presenting');
        const [presentationElements, setPresentationElements] = useState<PresentationElement[]>([]);
        const [blockResults, setBlockResults] = useState<ExperimentResults['blockResults']>([]);
        const [currentBlockIndex, setCurrentBlockIndex] = useState(0);
        const [numCorrect, setNumCorrect] = useState(0);
        const [numIncorrect, setNumIncorrect] = useState(0);
        const [numNotResponded, setNumNotResponded] = useState(0);
        const [customSequence, setCustomSequence] = useState<number[]>([]);
        const [isFetchingCustom, setIsFetchingCustom] = useState(false);
        const [transcription, setTranscription] = useState<string>("");

        // 3. HARDENED SPEECH RECOGNITION LOOP
        useEffect(() => {

            const isEnabled = (cs as any)?.enableTranscription === true;
            console.log("Checking mic status... Flag is:", isEnabled);

            if (isEnabled !== true) {
              //  console.warn("🎤 [Mic Bouncer] Microphone disabled or config not loaded yet.");
                return; // Bypass microphone logic
            }

            //console.log("🎤 [Mic Bouncer] Flag is TRUE! Booting engine...");

            if (!SpeechRecognition) {
                console.warn("Speech Recognition API is not supported in this browser.");
                return;
            }

            let isComponentMounted = true;
            let recognition: any = null;
            let restartTimeout: NodeJS.Timeout;

            const bootSpeechEngine = () => {
                // --- NEW: If the tab is hidden, or component unmounted, do NOT try to start ---
                if (!isComponentMounted || document.visibilityState === 'hidden') return;

                try {
                    recognition = new SpeechRecognition();
                    recognition.continuous = true; 
                    recognition.interimResults = true; 
                    recognition.lang = 'en-US';

                    recognition.onresult = (event: any) => {
                        let finalWords = "";
                        let pendingWords = "";

                        for (let i = event.resultIndex; i < event.results.length; i++) {
                            if (event.results[i].isFinal) {
                                finalWords += event.results[i][0].transcript + " ";
                            } else {
                                pendingWords += event.results[i][0].transcript + " ";
                            }
                        }

                        // 1. Grab permanent words
                        const existingText = localStorage.getItem("QUALTRICS_TRANSCRIPT_BASE") || "";
                        
                        // 2. Save new permanent words
                        if (finalWords.trim().length > 0) {
                            localStorage.setItem("QUALTRICS_TRANSCRIPT_BASE", existingText + finalWords);
                        }

                        // 3. Write the live stream to the exact key SaveData is looking for
                        const currentLiveTranscript = existingText + finalWords + pendingWords;
                        localStorage.setItem("QUALTRICS_TRANSCRIPT", currentLiveTranscript);
                        
                        //console.log("🗣️ ENGINE HEARD:", currentLiveTranscript);
                    };                

                    recognition.onend = () => {
                        if (!isComponentMounted) return;
                        // Engine died naturally. Wait 250ms and rebuild it (if we are still looking at the tab).
                        restartTimeout = setTimeout(bootSpeechEngine, 250);
                    };

                    recognition.onerror = (event: any) => {
                        if (event.error === 'no-speech') return; 
                        
                        if (recognition) {
                            try { recognition.stop(); } catch (e) {}
                        }
                    };

                    recognition.start();

                } catch (err) {
                    restartTimeout = setTimeout(bootSpeechEngine, 1000);
                }
            }

            // Start the infinite loop initially
            bootSpeechEngine();

            // --- NEW: Tab Visibility Listener ---
            // Wakes the engine up instantly when the participant clicks back to the experiment
            const handleVisibilityChange = () => {
                if (document.visibilityState === 'visible') {
                    //console.log("Tab regained focus. Waking up speech engine...");
                    // Clear any pending timeouts and immediately force a fresh boot
                    clearTimeout(restartTimeout);
                    bootSpeechEngine();
                } else {
                    //console.log("Tab lost focus. Putting speech engine to sleep...");
                    // Stop the engine gracefully so it doesn't crash into the privacy wall
                    if (recognition) {
                        try { recognition.stop(); } catch (e) {}
                    }
                }
            };

            document.addEventListener("visibilitychange", handleVisibilityChange);

            // --- CLEANUP ---
            return () => {
                isComponentMounted = false;
                clearTimeout(restartTimeout);
                document.removeEventListener("visibilitychange", handleVisibilityChange);
                if (recognition) {
                    recognition.onend = null; 
                    try { recognition.stop(); } catch (e) {}
                }
            };
        }, [cs]);

       // 2. SAFE MEMOS FOR STAGE AND BLOCKS
        const currentStage = useMemo(() => cs as TrialRunnerStage, [cs]);

        const blockOrder = useMemo(() => {
            if (!stimulusData) return []; 
            if (Array.isArray(currentStage.blockOrder)) return currentStage.blockOrder;
            if (currentStage.blockOrder === 'sequential' || currentStage.blockOrder == null) {
                return [...new Array(stimulusData.length).keys()];
            }
            if (currentStage.blockOrder === 'reversed') {
                return [...new Array(stimulusData.length).keys()].reverse();
            }
            return [...new Array(stimulusData.length).keys()].sort(() => 0.5 - Math.random());
        }, [currentStage, stimulusData]);

        experimentResults.blockOrder = blockOrder;
        const effectiveBlockIndex = blockOrder[currentBlockIndex] ?? 0;

        // 3. DESIGN BIN COUNTER EFFECT
        useEffect(() => {
            // --- CRITICAL FIX: Use .includes() to ignore hidden \r characters ---
            let designColumnIndex: number | undefined = undefined;
            if (headerToIndexMap) {
                for (const key of headerToIndexMap.keys()) {
                    if (typeof key === 'string' && key.toLowerCase().includes('log_freq')) {
                        designColumnIndex = headerToIndexMap.get(key);
                        break;
                    }
                }
            }
            
            const currentBlockRows = stimulusData?.[effectiveBlockIndex];

            if (designColumnIndex !== undefined && currentBlockRows) {
                const initialCounts: Record<number, number> = {};
                currentBlockRows.forEach((row: any) => {
                    const designValue = Number(row[designColumnIndex as number]);
                    if (!isNaN(designValue)) {
                        initialCounts[designValue] = (initialCounts[designValue] || 0) + 1;
                    }
                });
                setDesignCounts(initialCounts);
                //console.log("Dynamically loaded design bins:", initialCounts);
            }
        }, [effectiveBlockIndex, stimulusData, headerToIndexMap]);

		// 4. THE BULLETPROOF TRIAL ORDER 
        const trialOrder = useMemo(() => {
            const currentBlockTrials = stimulusData?.[effectiveBlockIndex];
            
            if (!currentBlockTrials || !Array.isArray(currentBlockTrials)) return [];
            if (currentBlockIndex >= blockOrder.length) return [];

            // --- Implement nTrials limit from expConfig.json ---
            let numTrials = currentBlockTrials.length;
            const customSettings = (currentStage as any).customTrialSettings || {};
            
            // --- Calculate grand total if nBlocks is provided ---
            let customMaxTrials = customSettings.nTrials; 
            if (customMaxTrials && customSettings.nBlocks) {
                customMaxTrials = customMaxTrials * customSettings.nBlocks; // 3 * 2 = 6 total
            }
            
            if (typeof customMaxTrials === 'number' && customMaxTrials > 0) {
                numTrials = Math.min(numTrials, customMaxTrials);
            }
            // -------------------------------------------------------------   
            // --------------------------------------------------------

            if (Array.isArray(currentStage.trialOrder)) return currentStage.trialOrder.slice(0, numTrials);
            
            if (currentStage.trialOrder === 'sequential' || currentStage.trialOrder == null || currentStage.trialOrder === 'custom') {
                return [...new Array(numTrials).keys()];
            }
            if (currentStage.trialOrder === 'reversed') {
                return [...new Array(currentBlockTrials.length).keys()].reverse().slice(0, numTrials);
            }

            return [...new Array(currentBlockTrials.length).keys()].sort(() => 0.5 - Math.random()).slice(0, numTrials);
        }, [currentStage, stimulusData, effectiveBlockIndex, blockOrder.length, currentBlockIndex]);

        const currentBlock = stimulusData?.[effectiveBlockIndex] ?? [];   


	// --- NEW: Reset custom sequence when moving to a new block ---
	useEffect(() => {
		/// setCustomSequence([]); Use this line to reset the stimlist to use all rows at the start of a new block. 
	}, [effectiveBlockIndex]);

	// --- NEW: Python API Fetch Logic ---
	useEffect(() => {
		if (currentStage.trialOrder !== 'custom') return;
		if (currentPhase !== 'presenting') return;
		if (customSequence.length > currentTrialIndex) return; 

		const fetchNextTrialFromPython = async () => {
            setIsFetchingCustom(true);
            try {
                const trialEndpoint = process.env.NODE_ENV === 'production' 
                    ? '/python-api/get-next-trial' 
                    : 'http://127.0.0.1:8000/get-next-trial';
                    
                
                const customSettings = (currentStage as any).customTrialSettings || {};
                const initEndpoint = customSettings.initEndpoint || (process.env.NODE_ENV === 'production' 
                    ? '/python-api/initialize-block' 
                    : 'http://127.0.0.1:8000/initialize-block');

                let activeWeights = modelState?.weights;

                // --- CHANGED: Pull from customSettings consistently ---
                const trialsPerBlock = customSettings.nTrials || 60; 

                // Check if the current trial index is perfectly divisible by trialsPerBlock (0, 2, 4, etc.)
                const isStartOfVirtualBlock = (currentTrialIndex % trialsPerBlock === 0);

                if (isStartOfVirtualBlock || !activeWeights) {
                    // console.log("Initializing Bayesian priors...");
                    const initRes = await fetch(initEndpoint, { method: 'POST' });
                    if (!initRes.ok) throw new Error("Failed to initialize block.");
                    
                    const initData = await initRes.json();
                    activeWeights = initData.weights;
                    setModelState(initData); 
                }

                const previousResponses = blockResults[effectiveBlockIndex]?.responses || [];
                const lastResponse = previousResponses[previousResponses.length - 1];

                // 1. BULLETPROOF COLUMN PICKER
                let designColumnIndex: number | undefined = undefined;
                if (headerToIndexMap) {
                    for (const key of headerToIndexMap.keys()) {
                        if (typeof key === 'string' && key.toLowerCase().includes('log_freq')) {
                            designColumnIndex = headerToIndexMap.get(key);
                            break;
                        }
                    }
                }

                let availableDesigns = Object.keys(designCounts).map(Number);
                
                // Safety Bins Catch-up
                if (availableDesigns.length === 0 && currentTrialIndex === 0) {
                    const currentBlockRows = stimulusData?.[effectiveBlockIndex];
                    if (designColumnIndex !== undefined && currentBlockRows) {
                        const tempCounts: Record<number, number> = {};
                        currentBlockRows.forEach((row: any) => {
                            const designValue = Number(row[designColumnIndex as number]);
                            if (!isNaN(designValue)) {
                                tempCounts[designValue] = (tempCounts[designValue] || 0) + 1;
                            }
                        });
                        availableDesigns = Object.keys(tempCounts).map(Number);
                        setDesignCounts(tempCounts); 
                    }
                }

                if (availableDesigns.length === 0) {
                    throw new Error("We have run out of available stimuli designs!");
                }

                // 2. EXTRACT PREVIOUS FLOAT VALUE FOR PYTHON PAYLOAD
				let lastTrialDesignValue = null;
				if (currentTrialIndex > 0 && designColumnIndex !== undefined) {
					const lastRowIdx = customSequence[currentTrialIndex - 1];
					
					// FIX 1: Ensure the index is explicitly defined before reading the row
					if (lastRowIdx !== undefined) {
						const lastRow = stimulusData?.[effectiveBlockIndex]?.[lastRowIdx];
						if (lastRow) {
							lastTrialDesignValue = Number(lastRow[designColumnIndex]);
						}
					}
				}

               const payload = {
                    // Send a relative index (0, 1, 2) instead of the global index
                    currentTrialIndex: currentTrialIndex % trialsPerBlock, 
                    
                    candidateFreqs: availableDesigns, 
                    currentWeights: activeWeights, 
                    
                    // Mask the previous trial data if we are at the start of a new block
                    lastTrialDesign: isStartOfVirtualBlock ? null : lastTrialDesignValue, 
                    lastTrialAcc: isStartOfVirtualBlock ? null : (lastResponse ? (lastResponse.answer === lastResponse.correctAnswer ? 1 : 0) : null)
                };

                //console.log("Sending data to Python:", payload);
                
                const response = await fetch(trialEndpoint, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });

                if (!response.ok) throw new Error("API responded with an error.");
                
                const data = await response.json();
                const chosenDesign = data.next_index; // e.g., 5.1
                // console.log(`[Python Bridge] Trial: ${currentTrialIndex} | Python Selected Float: ${chosenDesign}`);

                // 3. SMART MAPPING: Find an unused CSV row matching this float design
                let chosenRowIndex = -1;
                const currentBlockRows = stimulusData?.[effectiveBlockIndex];

                if (currentBlockRows && designColumnIndex !== undefined) {
                    const customSettings = (currentStage as any).customTrialSettings || {};
                    const uniqueRows = customSettings.uniqueRows === true;
                    
                    if (!uniqueRows) {
                        // --- STANDARD ADAPTIVE MODE ---
                        // Strict 1-to-1 mapping: Find the exact row for this design and lock it in
                        for (let i = 0; i < currentBlockRows.length; i++) {
                            const row = currentBlockRows[i];
                            if (row && Number(row[designColumnIndex]) === chosenDesign) {
                                chosenRowIndex = i;
                                break; // Stop immediately; this is the only row that matters
                            }
                        }
                    } else {
                        // --- WORD BANK MODE ---
                        // 1-to-Many mapping: Find all unused rows matching this frequency bin
                        const availableMatches: number[] = [];
                        
                        for (let i = 0; i < currentBlockRows.length; i++) {
                            const row = currentBlockRows[i];
                            if (row && Number(row[designColumnIndex]) === chosenDesign && !customSequence.includes(i)) {
                                availableMatches.push(i);
                            }
                        }

                        // Randomly draw one unused word from the matching bin
                        if (availableMatches.length > 0) {
                            const randomDraw = Math.floor(Math.random() * availableMatches.length);
                            chosenRowIndex = availableMatches[randomDraw]!;
                        }
                    }
                }        
                if (chosenRowIndex === -1) {
                    throw new Error(`Could not find an unused CSV row matching design value ${chosenDesign}`);
                }

                //console.log(`[Framework Match] Mapped design ${chosenDesign} to CSV row index: ${chosenRowIndex}`);

                // Decrement the bin tracker
                setDesignCounts(prevCounts => {
                    const updatedCounts = { ...prevCounts };
                    updatedCounts[chosenDesign] = (updatedCounts[chosenDesign] || 0) - 1;
                    if ((updatedCounts[chosenDesign] || 0) <= 0) {
                        delete updatedCounts[chosenDesign];
                    }
                    return updatedCounts;
                });

                setModelState({
                    weights: data.weights,
                    t_mean: data.t_mean,
                    t_sd: data.t_sd,
                    s_mean: data.s_mean,
                    s_sd: data.s_sd
                });
                
                // SUCCESS: Save the INTEGER row index so React can cleanly display the words!
                setCustomSequence(prev => [...prev, chosenRowIndex]);
                
            } catch(e) {
                console.error("Failed to fetch custom trial. Falling back to sequential order.", e);
                setCustomSequence(prev => [...prev, currentTrialIndex]);
            } finally {
                setIsFetchingCustom(false);
            }
        };

		fetchNextTrialFromPython();
	}, [currentTrialIndex, currentPhase, currentStage.trialOrder, effectiveBlockIndex]);
	// -----------------------------------------------

// --- UPDATED: Determine which index to actually use ---
	if (currentStage.trialOrder === 'custom') {
		// If customSequence hasn't loaded a value yet but we aren't loading, fallback safely to sequential index
		effectiveTrialIndex = customSequence[currentTrialIndex] !== undefined 
			? customSequence[currentTrialIndex] 
			: (isFetchingCustom ? undefined : currentTrialIndex);
	} else {
		effectiveTrialIndex = trialOrder[currentTrialIndex] as number;
	}

	currentRow = effectiveTrialIndex !== undefined ? currentBlock[effectiveTrialIndex] : undefined;
	// ------------------------------------------------------

	// --- CHANGED: Base the remaining trials on our capped trialOrder length, not the raw CSV ---
    const areTrialsLeftInBlock = (currentTrialIndex + 1) < trialOrder.length;


    // --- NEW: PUSH THE COMBINED TRIAL DATA TO THE TRACKER ---
    useEffect(() => {
        if (setActiveTrialIdentifier && effectiveTrialIndex !== undefined) {
            const chronologicalNumber = currentTrialIndex + 1;
            
            if (currentStage.trialOrder === 'custom') {
                // In adaptive mode, log both the chronological sequence and the mapped CSV row
                setActiveTrialIdentifier(`${chronologicalNumber},${effectiveTrialIndex}`);
            } else {
                // In standard modes, just log the chronological trial number
                setActiveTrialIdentifier(chronologicalNumber);
            }
        }
    }, [currentTrialIndex, effectiveTrialIndex, currentStage.trialOrder, setActiveTrialIdentifier]);
    // --------------------------------------------------------



	const goToNextBlock = () => {
		setCurrentBlockIndex((currentBlockIndex) => currentBlockIndex + 1);
		setCurrentTrialIndex(0);
	};

	const addPresentationElement = (element: PresentationElement) => {
		setPresentationElements((presentationElements) => [...presentationElements, element]);
	};

	const resetPresentationElements = () => {
		setPresentationElements([]);
	};

	const setPause = () => {
		const { presentations } = currentStage;
		const presentation = presentations[currentPresentationIndex];

		if (!presentation) throw new Error('No presentation');
		if (!currentRow) return; 
		
		switch (presentation.type) {
			case 'audio': {
				const fileColumnHeader = presentation.fileNameColumn as string;
				const fileColumnIndex = headerToIndexMap?.get(fileColumnHeader) as number;

				const fileName = currentRow[fileColumnIndex] as string;
				const audio = srcToAudioMap.get(fileName);
				if (!audio) throw new Error('bad');
				audio.pause();
				break;
			}
		}
	};

	const incrementTrialIndex = () => {
		if (areTrialsLeftInBlock) {
			setCurrentTrialIndex((currentTrialIndex) => currentTrialIndex + 1);
		} else {
			goToNextBlock();
		}
	};

const addTrialResponse = (
        answer: any,
        responseTime: number,
        url?: any,
        trialNumber?: number,
        correctAnswer?: string
    ) => {
        if (!currentRow) return;
        
        // --- CHANGED: Use 'let' instead of 'const' so we can override it ---
        let blockNumber = Number.parseInt(currentRow[0] as string);

        // --- SPOT 3: Override the block number ONLY for custom adaptive stages ---
        // --- SPOT 3: Override the block number ONLY for custom adaptive stages ---
        if (currentStage.trialOrder === 'custom') {
            const customSettings = (currentStage as any).customTrialSettings || {};
            const trialsPerBlock = customSettings.nTrials || 60;
            
            // Mathematically calculates the virtual block (0, 1, 2...)
            blockNumber = Math.floor(currentTrialIndex / trialsPerBlock);
        }
       
        // --- NEW: Forcefully grab the correct answer from the CSV row ---
        let actualCorrectAnswer = correctAnswer;
        if (headerToIndexMap) {
            const correctColIdx = headerToIndexMap.get('correctanswer'); // The exact name of your CSV column
            if (correctColIdx !== undefined) {
                actualCorrectAnswer = currentRow[correctColIdx] as string;
            }
        }

        setBlockResults((prevBlockResults) => {
            const newBlockResults = [...prevBlockResults];
            const currentBlockResult =
                newBlockResults[blockNumber] ??
                (() => {
                    const newBlockResult: typeof prevBlockResults[0] = {
                        blockNumber: blockNumber.toString(),
                        responses: []
                    };
                    newBlockResults[blockNumber] = newBlockResult;
                    return { blockNumber: blockNumber.toString(), responses: [] };
                })();

            // --- NEW: Extract the payload into a variable and log it! ---
            const trialResponsePayload = { 
                answer, 
                responseTime, 
                trialNumber, 
                correctAnswer: actualCorrectAnswer,
                t_mean: modelState?.t_mean ?? null,
                t_sd: modelState?.t_sd ?? null,
                s_mean: modelState?.s_mean ?? null,
                s_sd: modelState?.s_sd ?? null
            };

            //console.log(`[Trial Complete] Output Payload saved for Trial ${trialNumber ?? currentTrialIndex}:`, trialResponsePayload);

            currentBlockResult.responses.push(trialResponsePayload as any);

            newBlockResults[blockNumber] = currentBlockResult;
            return newBlockResults;
        });
    };

	const endPhase = () => {
		switch (currentPhase) {
			case 'presenting': {
				setCurrentPhase('responding');
				break;
			}
			case 'responding': {
				const noBreakScreens = (currentStage as TrialRunnerStage).breakScreen === undefined && false;
				const breaks = (currentStage as TrialRunnerStage).breakPositions
				if(breaks){
					if (breaks.indexOf(currentBlockIndex) > -1 && (currentTrialIndex === trialOrder.length - 1) && !noBreakScreens) {
						setCurrentPhase('break');
					} else {
						setCurrentPhase('presenting');
					}
					break;
				} else {
					if (currentTrialIndex !== trialOrder.length - 1 || noBreakScreens) {
						setCurrentPhase('presenting');
					} else {
						setCurrentPhase('break');
					}
					break;
				}
			}
			case 'break': {
				setCurrentPhase('presenting');
				break;
			}
			default: {
				throw new Error();
			}
		}
	};

	const incrementNumCorrect = () => {
		setNumCorrect((prevNumCorrect) => prevNumCorrect + 1);
	};

	const incrementNumIncorrect = () => {
		setNumIncorrect((prevNumIncorrect) => prevNumIncorrect + 1);
	};

	const incrementNumNotResponded = () => {
		setNumNotResponded((prevNumNotResponded) => prevNumNotResponded + 1);
	};

	const noMoreBlocks = currentBlockIndex === stimulusData?.length || (!currentRow && !isFetchingCustom);
	const isFeedback = Boolean(currentStage.feedback);

	return {
		currentTrialIndex,
		effectiveTrialIndex,

		currentPhase,
		currentStage,

		presentationElements,

		currentBlockIndex,
		effectiveBlockIndex,

		blockResults,

		currentRow,
		areTrialsLeftInBlock,

		goToNextBlock,
		noMoreBlocks,
		isFeedback,

		addPresentationElement,
		resetPresentationElements,
		setPause,
		incrementTrialIndex,
		addTrialResponse,
		endPhase,

		numCorrect,
		numIncorrect,
		numNotResponded,

		incrementNumCorrect,
		incrementNumIncorrect,
		incrementNumNotResponded,
		
		isFetchingCustom
	};
};

export const trialRunnerContext = createContext({} as ReturnType<typeof useValue>);
export const useTrialRunnerContext = () => useContext(trialRunnerContext);

export const TrialRunnerContextProvider: FC<Props> = (props) => (
	<trialRunnerContext.Provider value={useValue()}>{props.children}</trialRunnerContext.Provider>
);
